import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  Clock, 
  Copy, 
  QrCode,
  ArrowRight,
  Building2,
  CreditCard,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import theme from '../theme.config';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bank logos/colors
const BANK_INFO = {
  bca: { name: 'BCA', color: '#003d79', textColor: 'white' },
  bni: { name: 'BNI', color: '#f15a22', textColor: 'white' },
  bri: { name: 'BRI', color: '#00529c', textColor: 'white' },
  mandiri: { name: 'Mandiri', color: '#003366', textColor: 'white' },
  permata: { name: 'Permata', color: '#009a44', textColor: 'white' },
  cimb: { name: 'CIMB Niaga', color: '#ec1c24', textColor: 'white' },
};

export default function Checkout() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);
  const [processing, setProcessing] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('va');
  const [selectedBank, setSelectedBank] = useState('bni');
  const [paymentChannels, setPaymentChannels] = useState(null);
  const [creatingPayment, setCreatingPayment] = useState(false);

  useEffect(() => {
    if (!order && orderId) {
      toast.error('Order not found');
      navigate('/');
    }
    // Fetch payment channels
    fetchPaymentChannels();
  }, [orderId, order, navigate]);

  const fetchPaymentChannels = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payment/channels`);
      setPaymentChannels(response.data);
    } catch (error) {
      console.error('Failed to fetch payment channels:', error);
    }
  };

  const createPayment = async () => {
    if (!order) return;
    
    setCreatingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/api/payment/create`, {
        order_id: order.id,
        amount: order.price,
        customer_name: order.user_email?.split('@')[0] || 'Customer',
        customer_email: order.user_email,
        payment_method: selectedPaymentMethod,
        va_channel: selectedBank
      });
      
      if (response.data.success) {
        setPaymentData(response.data.data);
        toast.success('Payment successfully created!');
      } else {
        toast.error(response.data.message || 'Failed to create payment');
      }
    } catch (error) {
      console.error('Failed to create payment:', error);
      toast.error('Payment failed. Entering mock simulation mode.');
      // Fallback to mock mode
      setPaymentData({
        success: true,
        payment_method: selectedPaymentMethod,
        channel: selectedBank,
        va_number: '8810' + Math.random().toString().slice(2, 16),
        amount: order.price,
        is_mock: true
      });
    } finally {
      setCreatingPayment(false);
    }
  };

  const handlePayment = async () => {
    setProcessing(true);
    try {
      // Simulate payment processing
      const response = await axios.post(`${API_URL}/api/payment/process/${order.id}`);
      setOrder(response.data.order);
      toast.success('Payment settled successfully!');
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error('Payment verification failed');
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-[#09090b]">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const isCompleted = order.status === 'completed';

  return (
    <div className="min-h-screen pt-24 pb-12 bg-[#09090b] text-zinc-50 relative flex items-center">
      <div className="absolute inset-0 bg-[radial-gradient(#1c1c1f_1px,transparent_1px)] [background-size:16px_16px] opacity-10 pointer-events-none" />
      <div className="max-w-lg mx-auto px-4 w-full relative z-10">
        {isCompleted ? (
          /* Success State */
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-mono text-[10px] text-emerald-400 font-semibold tracking-wider uppercase">Transaction settled</span>
            </div>
            <h1 className="font-bold text-xl text-white mb-2">
              Transaction Succeeded!
            </h1>
            <p className="text-zinc-400 text-sm mb-8">
              Your digital voucher order has been routed and settled on the target destination.
            </p>

            <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6 text-left">
              <div className="space-y-4">
                <div className="flex justify-between border-b border-zinc-850 pb-2 text-xs">
                  <span className="text-zinc-500">Order Number</span>
                  <span className="font-mono text-white font-semibold">{order.order_number}</span>
                </div>
                <div className="flex justify-between border-b border-zinc-850 pb-2 text-xs">
                  <span className="text-zinc-500">Product</span>
                  <span className="text-white font-semibold">{order.product_name}</span>
                </div>
                <div className="flex justify-between border-b border-zinc-850 pb-2 text-xs">
                  <span className="text-zinc-500">Item</span>
                  <span className="text-white font-semibold">{order.denomination_name}</span>
                </div>
                <div className="flex justify-between border-b border-zinc-850 pb-2 text-xs">
                  <span className="text-zinc-500">Target ID</span>
                  <span className="font-mono text-white font-semibold">{order.game_user_id}</span>
                </div>
                {order.game_server_id && (
                  <div className="flex justify-between border-b border-zinc-850 pb-2 text-xs">
                    <span className="text-zinc-500">Server ID</span>
                    <span className="font-mono text-white font-semibold">{order.game_server_id}</span>
                  </div>
                )}
                <div className="pt-2 flex justify-between items-center text-xs">
                  <span className="text-zinc-500">Total Price</span>
                  <span className="font-bold text-white text-md">
                    {formatPrice(order.price)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1 border-zinc-850 text-white hover:bg-zinc-900 text-xs"
                onClick={() => navigate('/track')}
              >
                Check Status
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs"
                onClick={() => navigate('/')}
              >
                Back to Home
              </Button>
            </div>
          </div>
        ) : !paymentData ? (
          /* Payment Method Selection */
          <div>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                <span className="font-mono text-[10px] text-cyan-400 font-semibold tracking-wider uppercase">Awaiting payment channel</span>
              </div>
              <h1 className="font-bold text-xl text-white mb-2">
                Select Payment Option
              </h1>
              <p className="text-zinc-400 text-sm">
                Total Invoice: <span className="text-emerald-400 font-bold">{formatPrice(order.price)}</span>
              </p>
            </div>

            {/* Order Summary */}
            <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-850 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-zinc-950 flex items-center justify-center border border-zinc-800">
                  <CreditCard className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="font-bold text-white text-xs uppercase">{order.product_name}</p>
                  <p className="text-xs text-zinc-400">{order.denomination_name}</p>
                </div>
              </div>
              <div className="text-[10px] text-zinc-500 font-mono">
                Order Ref: {order.order_number}
              </div>
            </div>

            {/* Payment Method Tabs */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setSelectedPaymentMethod('va')}
                className={`flex-1 py-3 px-4 rounded-xl border text-xs font-semibold tracking-wide transition-all duration-200 ${
                  selectedPaymentMethod === 'va'
                    ? 'border-emerald-500 bg-emerald-500/5 text-white'
                    : 'border-zinc-800 bg-zinc-950/40 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <Building2 className="w-4 h-4 mx-auto mb-1.5" />
                <span>Virtual Account</span>
              </button>
              <button
                onClick={() => setSelectedPaymentMethod('qris')}
                className={`flex-1 py-3 px-4 rounded-xl border text-xs font-semibold tracking-wide transition-all duration-200 ${
                  selectedPaymentMethod === 'qris'
                    ? 'border-cyan-500 bg-cyan-500/5 text-white'
                    : 'border-zinc-800 bg-zinc-950/40 text-zinc-400 hover:border-zinc-700'
                }`}
              >
                <QrCode className="w-4 h-4 mx-auto mb-1.5" />
                <span>QRIS Scan</span>
              </button>
            </div>

            {/* Bank Selection for VA */}
            {selectedPaymentMethod === 'va' && (
              <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-850 mb-6">
                <p className="text-xs text-zinc-400 mb-3 font-semibold">Select VA Bank Route:</p>
                <div className="grid grid-cols-3 gap-2">
                  {paymentChannels?.virtual_account?.map((bank) => (
                    <button
                      key={bank.code}
                      onClick={() => setSelectedBank(bank.code)}
                      className={`py-3 px-2 rounded-lg border text-center transition-all ${
                        selectedBank === bank.code
                          ? 'border-emerald-500 bg-emerald-500/5'
                          : 'border-zinc-800 bg-zinc-950/40 hover:border-zinc-700'
                      }`}
                    >
                      <span 
                        className="inline-block px-2 py-0.5 rounded text-[10px] font-bold"
                        style={{ 
                          backgroundColor: BANK_INFO[bank.code]?.color || '#333',
                          color: BANK_INFO[bank.code]?.textColor || 'white'
                        }}
                      >
                        {BANK_INFO[bank.code]?.name || bank.code.toUpperCase()}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* QRIS Info */}
            {selectedPaymentMethod === 'qris' && (
              <div className="bg-zinc-900/30 rounded-xl p-4 border border-zinc-850 mb-6">
                <div className="flex items-center gap-3">
                  <QrCode className="w-8 h-8 text-cyan-400" />
                  <div>
                    <p className="font-bold text-white text-xs">QRIS Automated Route</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      Scan invoice barcode using GoPay, OVO, Dana or mobile banking.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-5 rounded-lg text-xs tracking-wider"
              onClick={createPayment}
              disabled={creatingPayment}
              data-testid="create-payment-btn"
            >
              {creatingPayment ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  Allocating invoice...
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  Proceed to Payment
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              )}
            </Button>
          </div>
        ) : (
          /* Payment Details */
          <div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Clock className="w-8 h-8 text-cyan-400" />
              </div>
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
                <span className="font-mono text-[10px] text-cyan-400 font-semibold tracking-wider uppercase">Awaiting Settlement</span>
              </div>
              <h1 className="font-bold text-xl text-white mb-2">
                Complete Payment
              </h1>
              <p className="text-zinc-400 text-sm">
                Please settle the payment in 24 hours to secure transaction routing.
              </p>
            </div>

            {/* Order Summary */}
            <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6">
              <div className="flex items-center justify-between mb-4 border-b border-zinc-850 pb-3 text-xs">
                <span className="text-zinc-500">Order ID</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-white font-semibold">{order.order_number}</span>
                  <button
                    onClick={() => copyToClipboard(order.order_number)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3 pt-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-zinc-500">Product</span>
                  <span className="text-white font-medium">{order.product_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Item</span>
                  <span className="text-white font-medium">{order.denomination_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-500">Target ID</span>
                  <span className="font-mono text-white">{order.game_user_id}</span>
                </div>
              </div>

              <div className="border-t border-zinc-850 mt-4 pt-4">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-zinc-500 font-medium">TOTAL INVOICE</span>
                  <span className="font-bold text-white text-md">
                    {formatPrice(order.price)}
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            {paymentData.payment_method === 'va' || paymentData.payment_method === 'virtual_account' ? (
              <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span 
                    className="px-3 py-1 rounded font-bold text-[10px] text-white"
                    style={{ 
                      backgroundColor: BANK_INFO[paymentData.channel]?.color || '#333',
                      color: BANK_INFO[paymentData.channel]?.textColor || 'white'
                    }}
                  >
                    {BANK_INFO[paymentData.channel]?.name || paymentData.channel?.toUpperCase()}
                  </span>
                  <span className="text-white text-xs font-semibold">Virtual Account Billing</span>
                </div>

                <div className="bg-zinc-950/50 rounded-lg p-4 mb-4 border border-zinc-850">
                  <p className="text-[10px] text-zinc-500 mb-2 font-mono">VIRTUAL ACCOUNT NUMBER</p>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-base text-white font-bold tracking-wider">
                      {paymentData.va_number}
                    </span>
                    <button
                      onClick={() => copyToClipboard(paymentData.va_number)}
                      className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors border border-emerald-500/20"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-xs">
                  <p className="text-zinc-400 font-medium">Payment Instructions:</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-zinc-300">
                    <li>Log in to your banking application or visit ATM</li>
                    <li>Select Virtual Account Transfer option</li>
                    <li>Enter VA number displayed above</li>
                    <li>Verify details and authorize transaction</li>
                  </ol>
                </div>

                {paymentData.is_mock && (
                  <p className="text-[10px] text-center text-yellow-500 mt-4 font-mono animate-pulse">
                    * MOCK PROTOCOL — Virtual Account simulated for testing
                  </p>
                )}
              </div>
            ) : (
              <div className="bg-zinc-900/30 rounded-xl p-6 border border-zinc-850 mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <QrCode className="w-5 h-5 text-cyan-400 animate-pulse" />
                  <span className="font-bold text-white text-xs">QRIS Invoice</span>
                </div>

                <div className="bg-white rounded-xl p-6 mb-4 flex justify-center border border-zinc-200">
                  {paymentData.qr_content ? (
                    <QRCodeCanvas
                      value={paymentData.qr_content}
                      size={220}
                      level="M"
                      marginSize={2}
                    />
                  ) : (
                    <div className="w-[220px] h-[220px] flex items-center justify-center">
                      <QrCode className="w-20 h-20 text-gray-300 animate-pulse" />
                    </div>
                  )}
                </div>

                <p className="text-center text-xs text-zinc-500">
                  Scan QR code with e-wallet or mobile banking app
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-5 rounded-lg text-xs tracking-wider"
                onClick={handlePayment}
                disabled={processing}
                data-testid="simulate-payment-btn"
              >
                {processing ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Confirming transaction...
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 justify-center">
                    I Have Paid
                    <CheckCircle2 className="w-4 h-4" />
                  </span>
                )}
              </Button>
              
              <Button
                variant="outline"
                className="w-full border-zinc-850 text-zinc-400 hover:text-white text-xs"
                onClick={() => setPaymentData(null)}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Change Payment Method
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
